/**
 * Reference catalogs — shared TypeScript types for Болезни/Вредители/Сорняки
 * + their mini-reference categories (AK-232/233/234).
 *
 * Mirrors the backend Pydantic shapes in
 * `backend/src/ikolhoz/api/v1/reference.py`. EN translations deferred
 * (RU-only tab in the UI) — `name_translations` JSONB is ready for more
 * languages later.
 */

export type NameTranslations = Record<string, string>;

// ---------- AK-257: per-culture images + affected crops ----------

/** Dictionaries that support per-culture photos. */
export type ImageDict = 'diseases' | 'pests' | 'weeds';

/** A crop affected by a disease/pest/weed (with photo count for chips). */
export interface AffectedCrop {
  crop_id: string;
  name: string | null;
  severity: string | null;
  image_count: number;
}

/** A single photo in an entity's pool, with its crop attachments (M:N). */
export interface ReferenceImage {
  id: string;
  url: string;
  sort_order: number;
  crop_ids: string[];
}

// ---------- Mini-reference category (disease/pest) ----------

export interface ReferenceCategory {
  id: string;
  code: string;
  name_translations: NameTranslations;
  sort_order: number;
  is_active: boolean;
  /** number of (non-archived) entities referencing this category (DELETE-guard). */
  usage_count: number;
}

export interface CategoriesEnvelope {
  items: ReferenceCategory[];
}

export interface CategoryCreatePayload {
  code: string;
  name_translations: NameTranslations;
  sort_order?: number;
  is_active?: boolean;
}

// ---------- Crop groups (AK-259 — Группа культур, level 2) ----------

/**
 * Биологическая культура (таблица `reference.crop_groups`, ранг `biological`
 * в дереве ADR-044). «Пшеница» — одна биологическая, «Пшеница озимая» и
 * «Пшеница яровая» — две ТОВАРНЫЕ культуры под ней.
 *
 * Миграция 111: биологические атрибуты (латынь, ботаника, шкала фенофаз, pH)
 * живут здесь и наследуются товарными культурами. Того, что реально
 * различается у озимой и яровой формы (вегетация, морозостойкость, вынос NPK,
 * нормы высева), здесь СОЗНАТЕЛЬНО нет.
 */
export interface CropGroup {
  id: string;
  code: string;
  name_translations: NameTranslations;
  category_id: string | null;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  latin_name?: string | null;
  botanical_family?: string | null;
  botanical_genus?: string | null;
  botanical_class?: string | null;
  phenophase_scale?: string | null;
  soil_ph_min?: number | null;
  soil_ph_max?: number | null;
  /** number of (non-archived) crops referencing this group (DELETE-guard). */
  usage_count: number;
}

export interface CropGroupsEnvelope {
  items: CropGroup[];
}

export interface CropGroupCreatePayload {
  code: string;
  name_translations: NameTranslations;
  category_id?: string | null;
  color?: string | null;
  sort_order?: number;
  is_active?: boolean;
  // Миграция 111 — биологические атрибуты (наследуются товарными культурами).
  latin_name?: string | null;
  botanical_family?: string | null;
  botanical_genus?: string | null;
  botanical_class?: string | null;
  phenophase_scale?: string | null;
  soil_ph_min?: number | null;
  soil_ph_max?: number | null;
}

// ---------- Paged list envelope (diseases/pests/weeds) ----------

export interface PagedResponse<T> {
  total: number;
  items: T[];
  page: number | null;
  page_size: number | null;
}

export interface ReferenceListQuery {
  search?: string;
  category?: string;
  /** weeds only */
  life_cycle?: string;
  limit?: number;
  offset?: number;
}

// ---------- Diseases ----------

export interface DiseaseListItem {
  id: string;
  name_translations: NameTranslations;
  latin_name: string | null;
  common_code: string | null;
  category: string;
  category_id: string | null;
  category_name: string | null;
  affected_parts: string[];
  image_urls: string[];
  /** «Другие названия» — народные/местные синонимы (AK-reference). */
  also_known_as: string[];
}

export interface DiseaseDetail extends DiseaseListItem {
  pathogen_type: string | null;
  optimal_temp_c_min: number | null;
  optimal_temp_c_max: number | null;
  optimal_humidity_pct_min: number | null;
  optimal_humidity_pct_max: number | null;
  key_symptoms_translations: NameTranslations | null;
  typical_yield_loss_pct_min: number | null;
  typical_yield_loss_pct_max: number | null;
}

export interface DiseasePayload {
  name_translations: NameTranslations;
  latin_name: string | null;
  common_code: string | null;
  /** legacy free-text category (NOT NULL in DB) — kept in sync with category code. */
  category: string;
  category_id: string | null;
  pathogen_type: string | null;
  optimal_temp_c_min: number | null;
  optimal_temp_c_max: number | null;
  optimal_humidity_pct_min: number | null;
  optimal_humidity_pct_max: number | null;
  affected_parts: string[];
  typical_yield_loss_pct_min: number | null;
  typical_yield_loss_pct_max: number | null;
  also_known_as: string[];
}

// ---------- Pests ----------

export interface PestListItem {
  id: string;
  name_translations: NameTranslations;
  latin_name: string | null;
  common_code: string | null;
  category: string;
  category_id: string | null;
  category_name: string | null;
  order_latin: string | null;
  family_latin: string | null;
  generations_per_year: number | null;
  image_urls: string[];
  /** «Другие названия» — народные/местные синонимы (AK-reference). */
  also_known_as: string[];
}

export interface PestDetail extends PestListItem {
  overwintering_stage: string | null;
  damage_type: string[];
  affected_parts: string[];
  economic_threshold_value: number | null;
  economic_threshold_unit: string | null;
}

export interface PestPayload {
  name_translations: NameTranslations;
  latin_name: string | null;
  common_code: string | null;
  category: string;
  category_id: string | null;
  order_latin: string | null;
  family_latin: string | null;
  generations_per_year: number | null;
  overwintering_stage: string | null;
  damage_type: string[];
  affected_parts: string[];
  economic_threshold_value: number | null;
  economic_threshold_unit: string | null;
  also_known_as: string[];
}

// ---------- Weeds ----------

/**
 * Категории и жизненные циклы сорняков — мини-справочники
 * (`reference.weed_categories` / `reference.weed_life_cycles`, migration 106),
 * редактируются в platform-admin. Захардкоженные WEED_CATEGORIES /
 * WEED_LIFE_CYCLES удалены: списки приходят из API
 * (`useWeedCategories` / `useWeedLifeCycles`), а подписи — из
 * `name_translations`. Тип строки — общий `ReferenceCategory`.
 */

export interface WeedListItem {
  id: string;
  name_translations: NameTranslations;
  latin_name: string | null;
  /** Легаси-текст = `code` строки справочника (фильтры API работают по нему). */
  category: string;
  category_id: string | null;
  /** RU-имя из reference.weed_categories (LEFT JOIN на бэке). */
  category_name: string | null;
  life_cycle: string;
  life_cycle_id: string | null;
  life_cycle_name: string | null;
  eppo_code: string | null;
  image_urls: string[];
  /** «Другие названия» — народные/местные синонимы (AK-reference). */
  also_known_as: string[];
}

export interface WeedDetail extends WeedListItem {
  family_latin: string | null;
  propagation_types: string[];
  is_parasitic: boolean;
  is_quarantine: boolean;
  is_invasive: boolean;
  economic_threshold_value: number | null;
  economic_threshold_unit: string | null;
  genus_latin: string | null;
}

export interface WeedPayload {
  name_translations: NameTranslations;
  latin_name: string | null;
  /** Держим синхронно с `code` выбранной строки справочника (бэк тоже сверяет). */
  category: string;
  category_id: string | null;
  life_cycle: string;
  life_cycle_id: string | null;
  family_latin: string | null;
  is_parasitic: boolean;
  is_quarantine: boolean;
  is_invasive: boolean;
  economic_threshold_value: number | null;
  economic_threshold_unit: string | null;
  genus_latin: string | null;
  also_known_as: string[];
}

/**
 * Подпись значения справочника сорняков по коду. Источник — загруженный
 * справочник; если строка не найдена (справочник ещё грузится / значение
 * осталось от легаси-текста без FK) — откатываемся на сам код, чтобы в UI
 * никогда не появлялся пустой бейдж.
 */
export function weedRefLabel(
  code: string | null | undefined,
  items: ReferenceCategory[] | undefined,
): string {
  if (!code) return '—';
  const hit = items?.find((c) => c.code === code);
  return hit?.name_translations.ru ?? code;
}

// ---------- Operation types (AK-238 — тех. операции) ----------

/**
 * Категории тех. операций — фиксированный enum (НЕ мини-справочник).
 * Совпадает с реальными значениями из seed_tech_ops_platform_refs.py.
 * Founder lock: операции группируются по этим категориям, не выдумываем
 * новые на лету. Вынос в мини-справочник — отдельная тема (сорняки свои
 * enum'ы уже перенесли в reference.weed_categories / weed_life_cycles).
 */
export const OPERATION_CATEGORIES = [
  { value: 'sowing', label: 'Посев' },
  { value: 'tillage', label: 'Обработка почвы' },
  { value: 'fertilizing', label: 'Внесение удобрений' },
  { value: 'protection', label: 'Защита растений' },
  { value: 'harvesting', label: 'Уборка' },
  { value: 'irrigation', label: 'Орошение' },
  // добавлена по решению founder 30.07 (класс logistics) — вердикт founder
  // по разбору 123 спорных типов операций поставил "➕ Логистика" на 12
  // тенантских строк (docs/superpowers/specs/2026-07-30-optype-verdicts-applied.md
  // §0 п.7); op_class='logistics' легален в reference.operation_types с
  // миграции 093, категория в этом enum'е ему соответствовала лишь текстом.
  // Не "выдумка на лету" — это founder-вердикт, зафиксированный в Excel.
  { value: 'logistics', label: 'Логистика' },
  { value: 'other', label: 'Прочее' },
] as const;

/**
 * close_kind — вид «закрывающей» операции в логике посевного окна.
 * Свободный текст в БД; здесь — типовые значения для select-подсказки.
 */
export const OPERATION_CLOSE_KINDS = [
  { value: 'harvest', label: 'Уборка урожая' },
  { value: 'termination', label: 'Уничтожение посева' },
  { value: 'replant', label: 'Пересев' },
] as const;

export function operationCategoryLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return OPERATION_CATEGORIES.find((c) => c.value === code)?.label ?? code;
}

/**
 * Один элемент динамической формы операции (fields_schema / work_fields_schema).
 * Зеркалит validate_fields_schema в reference.py.
 */
export interface OperationFieldSchemaItem {
  key: string;
  type: 'number' | 'string' | 'enum' | 'bool' | 'date';
  label: string;
  unit?: string;
  required?: boolean;
  min?: number;
  max?: number;
  options?: string[];
  help?: string;
  source?: string;
}

export interface OperationTypeListItem {
  id: string;
  code: string;
  country_code: string;
  name_translations: NameTranslations;
  description_translations: NameTranslations | null;
  category: string | null;
  typical_timing: string | null;
  is_sowing_start: boolean;
  is_sowing_close: boolean;
  close_kind: string | null;
  typical_machine_types: string[];
  typical_inputs: string[];
  fields_schema: OperationFieldSchemaItem[];
  work_fields_schema: OperationFieldSchemaItem[];
  schema_version: number;
}

/**
 * Detail = тот же shape, что и list (backend отдаёт полную карточку и в
 * списке, и по id). Алиас сохранён для совместимости с detail-fetch
 * паттерном (PR #290 B1) — edit-форма строится из detail-запроса.
 */
export type OperationTypeDetail = OperationTypeListItem;

export interface OperationTypeCreatePayload {
  code: string;
  country_code: string;
  name_translations: NameTranslations;
  description_translations: NameTranslations | null;
  category: string | null;
  typical_timing: string | null;
  is_sowing_start: boolean;
  is_sowing_close: boolean;
  close_kind: string | null;
  typical_machine_types: string[];
  typical_inputs: string[];
  fields_schema: OperationFieldSchemaItem[];
  work_fields_schema: OperationFieldSchemaItem[];
}

/** PATCH payload — code/country_code неизменяемы после создания. */
export type OperationTypeUpdatePayload = Omit<
  OperationTypeCreatePayload,
  'code' | 'country_code'
>;

// ---------- Units (reference.units — PK = code, not UUID) ----------

export interface Unit {
  code: string;
  symbol: string;
  name_translations: NameTranslations;
  category: string;
  display_order: number;
}

export interface UnitCreatePayload {
  code: string;
  symbol: string;
  name_translations: NameTranslations;
  category: string;
  display_order: number;
}

export interface UnitUpdatePayload {
  symbol?: string;
  name_translations?: NameTranslations;
  category?: string;
  display_order?: number;
}
